import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from '@mui/material';
import {
  NotificationsActive as AlertIcon,
  CheckCircle as ResolveIcon,
  Visibility as AcknowledgeIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  usePHAlerts,
  useAcknowledgeAlert,
  useResolveAlert,
  useAlertThresholds,
  useStoreAlertThreshold,
  useUpdateAlertThreshold,
  useDeleteAlertThreshold,
} from '../../hooks/usePreventiveHealth';
import { useQueryClient } from '@tanstack/react-query';
import { SelectChangeEvent } from '@mui/material';

// ── Constants ──────────────────────────────────────────────────────────────────

const SEVERITY_COLOR: Record<string, 'error' | 'warning' | 'info' | 'default'> = {
  critical: 'error',
  warning: 'warning',
  info: 'info',
};

const SEVERITY_BADGE: Record<string, string> = {
  critical: '#ef5350',
  warning: '#ffa726',
  info: '#42a5f5',
};

const STATUS_LABEL: Record<string, string> = {
  unacknowledged: 'Unacknowledged',
  acknowledged: 'Acknowledged',
  resolved: 'Resolved',
};

const THRESHOLD_TYPES = [
  {
    value: 'compliance_rate',
    label: 'Compliance Rate',
    unit: '%',
    description: 'Alert when compliance falls below threshold',
  },
  {
    value: 'missed_events',
    label: 'Missed Events',
    unit: 'events',
    description: 'Alert when consecutive missed events exceed threshold',
  },
  {
    value: 'upcoming_event_days',
    label: 'Upcoming Event Reminder',
    unit: 'days',
    description: 'Alert N days before a scheduled event',
  },
  {
    value: 'withdrawal_expiry_days',
    label: 'Withdrawal Period Expiry',
    unit: 'days',
    description: 'Alert N days before withdrawal period ends',
  },
  {
    value: 'overdue_hours',
    label: 'Overdue Event',
    unit: 'hours',
    description: 'Alert when an event is overdue by N hours',
  },
];

// ── Resolve Dialog ─────────────────────────────────────────────────────────────

interface ResolveDialogProps {
  alertId: number | null;
  onClose: () => void;
  onSubmit: (note: string) => void;
  loading: boolean;
}

const ResolveDialog: React.FC<ResolveDialogProps> = ({ alertId, onClose, onSubmit, loading }) => {
  const [note, setNote] = useState('');
  return (
    <Dialog open={!!alertId} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Resolve Alert</DialogTitle>
      <DialogContent>
        <TextField
          label="Resolution Note (optional)"
          multiline
          rows={3}
          fullWidth
          value={note}
          onChange={(e) => setNote(e.target.value)}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => onSubmit(note)} disabled={loading}>
          {loading ? <CircularProgress size={18} /> : 'Resolve'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Threshold Dialog ───────────────────────────────────────────────────────────

const BLANK_THRESHOLD = {
  type: '',
  value: '',
  severity: 'warning',
  is_active: true,
  description: '',
};

interface ThresholdDialogProps {
  open: boolean;
  initial: any;
  onClose: () => void;
  onSave: (data: any) => void;
  loading: boolean;
}

const ThresholdDialog: React.FC<ThresholdDialogProps> = ({
  open,
  initial,
  onClose,
  onSave,
  loading,
}) => {
  const [form, setForm] = useState(initial);

  React.useEffect(() => {
    setForm(initial);
  }, [initial]);

  const selectedType = THRESHOLD_TYPES.find((t) => t.value === form.type);

  const set = (field: string, val: any) => setForm((f: any) => ({ ...f, [field]: val }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initial.id ? 'Edit Threshold' : 'New Threshold'}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2.5} mt={1}>
          <FormControl fullWidth size="small">
            <InputLabel>Threshold Type</InputLabel>
            <Select
              value={form.type}
              label="Threshold Type"
              onChange={(e: SelectChangeEvent<string>) => set('type', e.target.value)}
            >
              {THRESHOLD_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedType && (
            <Typography variant="caption" color="text.secondary">
              {selectedType.description}
            </Typography>
          )}

          <TextField
            label={`Threshold Value${selectedType ? ` (${selectedType.unit})` : ''}`}
            type="number"
            size="small"
            fullWidth
            value={form.value}
            onChange={(e) => set('value', e.target.value)}
          />

          <FormControl fullWidth size="small">
            <InputLabel>Severity</InputLabel>
            <Select
              value={form.severity}
              label="Severity"
              onChange={(e: SelectChangeEvent<string>) => set('severity', e.target.value)}
            >
              <MenuItem value="critical">Critical</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="info">Info</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Description (optional)"
            size="small"
            fullWidth
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
          />

          <FormControlLabel
            control={
              <Switch
                checked={!!form.is_active}
                onChange={(e) => set('is_active', e.target.checked)}
              />
            }
            label="Active"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onSave(form)}
          disabled={loading || !form.type || !form.value}
        >
          {loading ? <CircularProgress size={18} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Alerts List ────────────────────────────────────────────────────────────────

const AlertsList: React.FC = () => {
  const qc = useQueryClient();
  const [severityFilter, setSeverityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('unacknowledged');
  const [resolveId, setResolveId] = useState<number | null>(null);

  const { data, isLoading, isError } = usePHAlerts({
    severity: severityFilter || undefined,
    status: statusFilter || undefined,
    per_page: 100,
  });

  const acknowledge = useAcknowledgeAlert();
  const resolve = useResolveAlert();

  const alerts: any[] = React.useMemo(
    () => (Array.isArray(data) ? data : (data?.data ?? [])),
    [data]
  );

  const handleResolve = (note: string) => {
    if (!resolveId) return;
    resolve.mutate({ id: resolveId, note }, { onSuccess: () => setResolveId(null) });
  };

  return (
    <Box>
      <Box display="flex" gap={2} mb={3} alignItems="center" justifyContent="space-between">
        <Box display="flex" gap={2} alignItems="center">
          <FilterIcon sx={{ color: 'text.secondary' }} />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Severity</InputLabel>
            <Select
              value={severityFilter}
              label="Severity"
              onChange={(e: SelectChangeEvent<string>) => setSeverityFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="info">Info</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e: SelectChangeEvent<string>) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="unacknowledged">Unacknowledged</MenuItem>
              <MenuItem value="acknowledged">Acknowledged</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={() => qc.invalidateQueries({ queryKey: ['ph', 'alerts'] })}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {isLoading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}
      {isError && <Alert severity="error">Failed to load alerts.</Alert>}
      {!isLoading && !isError && alerts.length === 0 && (
        <Alert severity="success">No alerts match the current filters.</Alert>
      )}

      {!isLoading && alerts.length > 0 && (
        <Box display="flex" flexDirection="column" gap={2}>
          {alerts.map((alert: any) => (
            <Paper
              key={alert.id}
              sx={{
                p: 2.5,
                borderLeft: `4px solid ${SEVERITY_BADGE[alert.severity] ?? '#555'}`,
                border: `1px solid ${SEVERITY_BADGE[alert.severity] ?? '#555'}33`,
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                <Box flex={1}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Chip
                      label={alert.severity}
                      size="small"
                      color={SEVERITY_COLOR[alert.severity] ?? 'default'}
                      sx={{ fontWeight: 700, textTransform: 'capitalize' }}
                    />
                    <Chip
                      label={STATUS_LABEL[alert.status] ?? alert.status}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 500, textTransform: 'capitalize' }}
                    />
                  </Box>
                  <Typography fontWeight={600} mb={0.5}>
                    {alert.message ?? alert.title}
                  </Typography>
                  {alert.batch_name && (
                    <Typography variant="body2" color="text.secondary">
                      Batch: {alert.batch_name}
                    </Typography>
                  )}
                  {alert.event_name && (
                    <Typography variant="body2" color="text.secondary">
                      Event: {alert.event_name}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {alert.created_at}
                  </Typography>
                </Box>
                <Box display="flex" gap={1} flexShrink={0}>
                  {alert.status === 'unacknowledged' && (
                    <Tooltip title="Acknowledge">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AcknowledgeIcon />}
                        onClick={() => acknowledge.mutate(alert.id)}
                        disabled={acknowledge.isPending}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        Acknowledge
                      </Button>
                    </Tooltip>
                  )}
                  {alert.status !== 'resolved' && (
                    <Tooltip title="Resolve">
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<ResolveIcon />}
                        onClick={() => setResolveId(alert.id)}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        Resolve
                      </Button>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      <ResolveDialog
        alertId={resolveId}
        onClose={() => setResolveId(null)}
        onSubmit={handleResolve}
        loading={resolve.isPending}
      />
    </Box>
  );
};

// ── Threshold Configuration ────────────────────────────────────────────────────

const ThresholdConfig: React.FC = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const { data, isLoading } = useAlertThresholds();
  const store = useStoreAlertThreshold();
  const update = useUpdateAlertThreshold();
  const remove = useDeleteAlertThreshold();

  const thresholds: any[] = React.useMemo(
    () => (Array.isArray(data) ? data : (data?.data ?? [])),
    [data]
  );

  const openNew = () => {
    setEditing({ ...BLANK_THRESHOLD });
    setDialogOpen(true);
  };

  const openEdit = (row: any) => {
    setEditing({ ...row });
    setDialogOpen(true);
  };

  const handleSave = (form: any) => {
    const payload = {
      type: form.type,
      value: parseFloat(form.value),
      severity: form.severity,
      is_active: form.is_active,
      description: form.description,
    };
    if (form.id) {
      update.mutate({ id: form.id, data: payload }, { onSuccess: () => setDialogOpen(false) });
    } else {
      store.mutate(payload, { onSuccess: () => setDialogOpen(false) });
    }
  };

  const handleToggleActive = (row: any) => {
    update.mutate({ id: row.id, data: { is_active: !row.is_active } });
  };

  const handleDelete = () => {
    if (!deleteConfirm) return;
    remove.mutate(deleteConfirm, { onSuccess: () => setDeleteConfirm(null) });
  };

  const typeLabel = (type: string) => THRESHOLD_TYPES.find((t) => t.value === type)?.label ?? type;

  const typeUnit = (type: string) => THRESHOLD_TYPES.find((t) => t.value === type)?.unit ?? '';

  if (isLoading)
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="body2" color="text.secondary">
          Configure when alerts are triggered for your farm. Changes take effect immediately.
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
          Add Threshold
        </Button>
      </Box>

      {thresholds.length === 0 && (
        <Alert severity="info">
          No thresholds configured. Add one to start receiving automated alerts.
        </Alert>
      )}

      {thresholds.length > 0 && (
        <TableContainer component={Paper} sx={{ border: '1px solid rgba(255,152,0,0.2)' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, color: '#ff9800' } }}>
                <TableCell>Type</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {thresholds.map((row: any) => (
                <TableRow key={row.id} hover>
                  <TableCell>{typeLabel(row.type)}</TableCell>
                  <TableCell>
                    {row.value} {typeUnit(row.type)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.severity}
                      size="small"
                      color={SEVERITY_COLOR[row.severity] ?? 'default'}
                      sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', maxWidth: 220 }}>
                    {row.description || '—'}
                  </TableCell>
                  <TableCell>
                    <Switch
                      size="small"
                      checked={!!row.is_active}
                      onChange={() => handleToggleActive(row)}
                      disabled={update.isPending}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(row)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => setDeleteConfirm(row.id)}
                        sx={{ '&:hover': { color: '#ef5350' } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ThresholdDialog
        open={dialogOpen}
        initial={editing ?? BLANK_THRESHOLD}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        loading={store.isPending || update.isPending}
      />

      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Threshold</DialogTitle>
        <DialogContent>
          <Typography>This threshold will be permanently removed. Are you sure?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={remove.isPending}
          >
            {remove.isPending ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ── Main Page ──────────────────────────────────────────────────────────────────

const AlertsDashboard: React.FC = () => {
  const [tab, setTab] = useState(0);
  const { data } = usePHAlerts({ status: 'unacknowledged', per_page: 1 });
  const unacknowledgedCount =
    (Array.isArray(data) ? data : (data?.data ?? [])).length || (data as any)?.meta?.total || 0;

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" gap={1.5} mb={3}>
        <AlertIcon sx={{ color: '#ff9800', fontSize: 28 }} />
        <Typography variant="h5" fontWeight={700}>
          Preventive Health Alerts
        </Typography>
        {unacknowledgedCount > 0 && (
          <Chip label={unacknowledgedCount} color="error" size="small" sx={{ fontWeight: 700 }} />
        )}
      </Box>

      <Paper sx={{ px: 2 }}>
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          sx={{ borderBottom: '1px solid rgba(255,152,0,0.15)' }}
        >
          <Tab icon={<AlertIcon />} iconPosition="start" label="Alerts" />
          <Tab icon={<SettingsIcon />} iconPosition="start" label="Threshold Configuration" />
        </Tabs>
        <Box py={3}>
          {tab === 0 && <AlertsList />}
          {tab === 1 && <ThresholdConfig />}
        </Box>
      </Paper>
    </Box>
  );
};

export default AlertsDashboard;
